import { useState, useEffect, type FormEvent } from 'react';
import { api } from '../api/public';
import s from './LeadForm.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  planId: number | null;
  planName: string;
}

export function LeadForm({ open, onClose, planId, planName }: Props) {
  const [form, setForm] = useState({ name: '', email: '', company_name: '', tel: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setForm({ name: '', email: '', company_name: '', tel: '' });
      setSuccess(false);
      setError('');
    }
  }, [open]);

  const update = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.submitLead({
        name: form.name,
        email: form.email,
        company_name: form.company_name || null,
        phone: form.tel || null,
        plan_interest: planId,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '送信に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <button className={s.close} onClick={onClose} aria-label="閉じる">&#10005;</button>

        {success ? (
          <div className={s.success}>
            <span className={s.successIcon}>&#10003;</span>
            <h3 className={s.successTitle}>お申し込みありがとうございます</h3>
            <p className={s.successDesc}>
              担当者より2営業日以内にご連絡いたします。<br />
              しばらくお待ちください。
            </p>
          </div>
        ) : (
          <>
            <h2 className={s.title}>プランのお申し込み</h2>
            <p className={s.desc}>以下の情報をご入力ください。担当者よりご連絡いたします。</p>
            {planName && <span className={s.planBadge}>{planName}</span>}

            {error && <div className={s.error}>{error}</div>}

            <form className={s.form} onSubmit={handleSubmit}>
              <div className={s.field}>
                <label className={s.label}>名前</label>
                <input
                  className={s.input}
                  type="text"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className={s.field}>
                <label className={s.label}>メールアドレス</label>
                <input
                  className={s.input}
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  required
                />
              </div>
              <div className={s.field}>
                <label className={s.label}>会社名<span className={s.labelOptional}>（任意）</span></label>
                <input
                  className={s.input}
                  type="text"
                  value={form.company_name}
                  onChange={(e) => update('company_name', e.target.value)}
                />
              </div>
              <div className={s.field}>
                <label className={s.label}>電話番号<span className={s.labelOptional}>（任意）</span></label>
                <input
                  className={s.input}
                  type="tel"
                  value={form.tel}
                  onChange={(e) => update('tel', e.target.value)}
                />
              </div>
              <button className={s.btn} type="submit" disabled={loading}>
                {loading ? '送信中...' : '送信する'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
